import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DomainLinkView, readDomainLink, verifyDomainLink } from '../api.ts';
import { LocalePicker } from '../components/LocalePicker.tsx';
import { ThemePicker } from '../components/ThemePicker.tsx';
import { formatWhen } from '../format.ts';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { useLoad } from '../hooks/useLoad.ts';
import { errorMessage } from '../messages.ts';

/**
 * La pagina del referente tecnico (mock 5, `o-tech`).
 *
 * "Chi tocca il DNS non è il presidente e non deve creare un accesso per
 * farlo" — è la schermata gemella di `InvitePage`, aperta senza account da chi
 * ha ricevuto un collegamento, non da chi entra nello staff. Riusa lo stesso
 * calcolo (`cnameInstructions`) e lo stesso pulsante ("verifica adesso") che
 * il fornitore già usa in `PlatformPage`.
 *
 * Non fa entrare nessuno: non c'è nessun modulo, nessuna password, nessuna
 * conseguenza oltre a rileggere lo stato del dominio. Per questo, a differenza
 * di `InvitePage`, il token **non** si toglie dall'indirizzo dopo la prima
 * lettura — chi la apre potrebbe voler ricaricare la pagina più tardi per
 * verificare di nuovo, ed è esattamente l'uso previsto (`docs/DECISIONI.md`,
 * AZ4).
 */

type Props = { token: string };

export function DomainLinkPage({ token }: Props) {
  const { t, i18n } = useTranslation();
  const { say, announcer } = useAnnounce();
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<{ status: string; checked: boolean } | undefined>();

  const read = useCallback((signal: AbortSignal) => readDomainLink(token, { signal }), [token]);
  const { data: link, failed } = useLoad<DomainLinkView>(read, {
    onError: (error) => say(errorMessage(t, error), 'danger'),
  });

  const verify = async (): Promise<void> => {
    if (verifying) return;
    setVerifying(true);
    try {
      const outcome = await verifyDomainLink(token);
      setResult(outcome);
      const key = outcome.checked
        ? `domain_link.verify_result_${outcome.status}`
        : 'domain_link.verify_unknown';
      say(
        t(key, { name: link?.cname.name ?? '' }),
        outcome.status === 'unresolved' ? 'danger' : undefined,
      );
    } catch (error) {
      say(`${t('domain_link.verify_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setVerifying(false);
    }
  };

  const status = result?.status ?? link?.status;

  /**
   * Il fuoco segue il titolo quando arriva davvero, non un contenitore che
   * lo aspetta ancora: chi apre il collegamento da uno screen reader deve
   * sentire il nome dell'ente appena è pronto, come in `InvitePage` (AM4).
   */
  const focusOnAppear = useCallback((node: HTMLHeadingElement | null) => {
    node?.focus();
  }, []);

  return (
    <main className="page page--narrow" id="main">
      <div className="card">
        <h1 ref={link ? focusOnAppear : undefined} tabIndex={-1}>
          {link ? t('domain_link.title', { tenant: link.tenant.name }) : t('admin.loading')}
        </h1>

        {announcer}

        {!link && !failed ? <p className="muted">{t('admin.loading')}</p> : null}
        {failed ? <p className="muted">{t('domain_link.failed')}</p> : null}

        {link ? (
          <>
            <p className="muted">{t('domain_link.subtitle')}</p>

            <dl className="facts">
              <div>
                <dt>{t('domain_link.field_type')}</dt>
                <dd>{link.cname.type}</dd>
              </div>
              <div>
                <dt>{t('domain_link.field_name')}</dt>
                <dd>{link.cname.name}</dd>
              </div>
              <div>
                <dt>{t('domain_link.field_value')}</dt>
                <dd>{link.cname.value}</dd>
              </div>
              <div>
                <dt>{t('domain_link.status_label')}</dt>
                <dd>{t(`platform.domain_status_${status ?? 'pending'}`)}</dd>
              </div>
              <div>
                <dt>{t('domain_link.certificate_label')}</dt>
                <dd>
                  {link.certificateExpiresAt
                    ? formatWhen(link.certificateExpiresAt, i18n.resolvedLanguage, undefined)
                    : t('domain_link.certificate_automatic')}
                </dd>
              </div>
            </dl>

            <button
              type="button"
              className="button button--primary"
              disabled={verifying}
              onClick={() => void verify()}
            >
              {verifying ? t('domain_link.verify_checking') : t('domain_link.verify_button')}
            </button>

            <p className="muted">
              {t('domain_link.expires_hint', {
                when: formatWhen(link.expiresAt, i18n.resolvedLanguage, undefined),
              })}
            </p>
          </>
        ) : null}
      </div>

      <div className="prefs-bar">
        <LocalePicker />
        <ThemePicker />
      </div>
    </main>
  );
}
